import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DollarSign, Calendar, CheckCircle2, Clock, XCircle, LogOut, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function IntegranteDashboard({ user }) {
  const navigate = useNavigate();
  const [cuotas, setCuotas] = useState([]);
  const [asistencias, setAsistencias] = useState([]);
  const [resumenAsistencia, setResumenAsistencia] = useState(null);
  const [estudiante, setEstudiante] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Buscar estudiante por email del usuario
      const estudianteResp = await axios.get(`${API}/estudiantes`, { withCredentials: true });
      const miEstudiante = estudianteResp.data.find(e => e.email === user.email);
      
      if (miEstudiante) {
        setEstudiante(miEstudiante);
        await Promise.all([
          loadCuotas(miEstudiante.id),
          loadAsistencias()
        ]);
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const loadCuotas = async (estudianteId) => {
    try {
      const response = await axios.get(`${API}/cuotas`, {
        params: {
          entidad_tipo: 'academia',
          entidad_id: estudiante?.academia_id || '',
          integrante_id: estudianteId
        },
        withCredentials: true
      });
      setCuotas(response.data || []);
    } catch (error) {
      console.error('Error al cargar cuotas');
    }
  };

  const loadAsistencias = async () => {
    try {
      const response = await axios.get(`${API}/mi-asistencia`, { withCredentials: true });
      setAsistencias(response.data.asistencias || []);
      setResumenAsistencia(response.data.resumen || null);
    } catch (error) {
      console.error('Error al cargar asistencias');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate('/academias');
    } catch (error) {
      toast.error('Error al cerrar sesión');
    }
  };

  const getEstadoBadge = (estado) => {
    const badges = {
      pendiente: { icon: Clock, color: 'bg-amber-100 text-amber-800', text: 'Pendiente' },
      pagada: { icon: CheckCircle2, color: 'bg-green-100 text-green-800', text: 'Pagada' },
      vencida: { icon: XCircle, color: 'bg-red-100 text-red-800', text: 'Vencida' }
    };
    
    const badge = badges[estado] || badges.pendiente;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.text}
      </span>
    );
  };

  const formatMonto = (monto) => {
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(monto);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!estudiante) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-slate-100 p-4">
        <Card className="max-w-2xl mx-auto p-8 text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">No se encontró tu perfil</h2>
          <p className="text-slate-600 mb-6">No pudimos encontrar tu registro como estudiante. Contacta al administrador.</p>
          <Button onClick={handleLogout}>Cerrar sesión</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-slate-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                Hola, {estudiante.nombre}
              </h1>
              <p className="text-slate-600">Portal del Integrante</p>
            </div>
            <Button onClick={handleLogout} variant="outline" className="gap-2">
              <LogOut className="w-4 h-4" />
              Cerrar sesión
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Tabs defaultValue="cuotas" className="space-y-6">
          <TabsList className="bg-white">
            <TabsTrigger value="cuotas" className="gap-2">
              <DollarSign className="w-4 h-4" />
              Mis Cuotas
            </TabsTrigger>
            <TabsTrigger value="asistencia" className="gap-2">
              <Calendar className="w-4 h-4" />
              Asistencia
            </TabsTrigger>
          </TabsList>

          {/* Tab: Cuotas */}
          <TabsContent value="cuotas">
            <div className="space-y-4">
              {/* Resumen */}
              <div className="grid md:grid-cols-3 gap-4">
                <Card className="p-6 bg-gradient-to-br from-green-50 to-green-100">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-green-700 font-medium">Pagadas</p>
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </div>
                  <p className="text-3xl font-bold text-green-900">
                    {cuotas.filter(c => c.estado === 'pagada').length}
                  </p>
                </Card>

                <Card className="p-6 bg-gradient-to-br from-amber-50 to-amber-100">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-amber-700 font-medium">Pendientes</p>
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                  <p className="text-3xl font-bold text-amber-900">
                    {cuotas.filter(c => c.estado === 'pendiente').length}
                  </p>
                </Card>

                <Card className="p-6 bg-gradient-to-br from-red-50 to-red-100">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-red-700 font-medium">Vencidas</p>
                    <XCircle className="w-5 h-5 text-red-600" />
                  </div>
                  <p className="text-3xl font-bold text-red-900">
                    {cuotas.filter(c => c.estado === 'vencida').length}
                  </p>
                </Card>
              </div>

              {/* Lista de Cuotas */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Historial de Cuotas</h3>
                
                {cuotas.length === 0 ? (
                  <div className="text-center py-12">
                    <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No hay cuotas registradas</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {cuotas.map((cuota) => (
                      <Card key={cuota.id} className="p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className="font-semibold text-slate-900">
                                {cuota.descripcion || `Cuota ${cuota.tipo}`}
                              </h4>
                              {getEstadoBadge(cuota.estado)}
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div>
                                <span className="text-slate-600">Monto: </span>
                                <span className="font-semibold text-slate-900">
                                  {formatMonto(cuota.monto)}
                                </span>
                              </div>
                              
                              {cuota.fecha_vencimiento && (
                                <div>
                                  <span className="text-slate-600">Vencimiento: </span>
                                  <span className="font-semibold text-slate-900">
                                    {cuota.fecha_vencimiento}
                                  </span>
                                </div>
                              )}
                              
                              <div>
                                <span className="text-slate-600">Tipo: </span>
                                <span className="capitalize text-slate-900">{cuota.tipo}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* Tab: Asistencia */}
          <TabsContent value="asistencia">
            <div className="space-y-4">
              {/* Resumen de Asistencia */}
              {resumenAsistencia && (
                <div className="grid md:grid-cols-4 gap-4">
                  <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-blue-700 font-medium">Total Clases</p>
                      <Calendar className="w-5 h-5 text-blue-600" />
                    </div>
                    <p className="text-3xl font-bold text-blue-900">
                      {resumenAsistencia.total_clases}
                    </p>
                  </Card>

                  <Card className="p-6 bg-gradient-to-br from-green-50 to-green-100">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-green-700 font-medium">Presente</p>
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    </div>
                    <p className="text-3xl font-bold text-green-900">
                      {resumenAsistencia.presentes}
                    </p>
                  </Card>

                  <Card className="p-6 bg-gradient-to-br from-red-50 to-red-100">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-red-700 font-medium">Ausente</p>
                      <XCircle className="w-5 h-5 text-red-600" />
                    </div>
                    <p className="text-3xl font-bold text-red-900">
                      {resumenAsistencia.ausentes}
                    </p>
                  </Card>

                  <Card className="p-6 bg-gradient-to-br from-purple-50 to-purple-100">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-purple-700 font-medium">% Asistencia</p>
                      <TrendingUp className="w-5 h-5 text-purple-600" />
                    </div>
                    <p className="text-3xl font-bold text-purple-900">
                      {resumenAsistencia.porcentaje_asistencia}%
                    </p>
                  </Card>
                </div>
              )}

              {/* Lista de Asistencias */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Historial de Asistencia</h3>
                
                {asistencias.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No hay registros de asistencia aún</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {asistencias.map((asist) => (
                      <Card key={asist.id} className="p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              asist.presente 
                                ? 'bg-green-100 text-green-600' 
                                : 'bg-red-100 text-red-600'
                            }`}>
                              {asist.presente 
                                ? <CheckCircle2 className="w-5 h-5" /> 
                                : <XCircle className="w-5 h-5" />}
                            </div>
                            <div>
                              <h4 className="font-semibold text-slate-900">
                                {asist.clase_nombre}
                              </h4>
                              <div className="flex items-center gap-3 text-sm text-slate-600">
                                <span>{asist.fecha}</span>
                                {asist.hora_inicio && (
                                  <span>{asist.hora_inicio} - {asist.hora_fin}</span>
                                )}
                                {asist.clase_dia && (
                                  <span className="capitalize">{asist.clase_dia}</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            asist.presente 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {asist.presente ? 'Presente' : 'Ausente'}
                          </span>
                        </div>
                        {asist.comentario_instructor && (
                          <p className="text-sm text-slate-500 mt-2 pl-14">
                            Nota: {asist.comentario_instructor}
                          </p>
                        )}
                      </Card>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
